// SceneryPlacementCount — the UI's "Placed: X" readout for a scenery feature (2026-08-28).
//
// Computes how many scenery instances a single REGION would place, using the EXACT same pipeline
// the live renderer uses (collectSceneryRegions over the packaged terrain-objects → 
// placeSceneryTile over the region's bbox at full density / mpp 0). This is the "intrinsic"
// density of the feature — independent of the camera/zoom (rank-thinning), so it's directly
// comparable to the fixture-based replication test counts.
//
// ⚠ The count is the region's own contribution (via `placedByRegion`), NOT the total over the
// tile — so it stays meaningful even when the region overlaps others.
import {MapObject_ForTerrain} from "../../Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain.js";
import {collectSceneryRegions, SceneryRegion} from "./SceneryRegions.js";
import {placeSceneryTile, PlacementKindLike} from "./SceneryPlacer.js";
import {SceneryBudgetConfig} from "../@SceneryBudgetConfig.js";
import {ResolvedSceneryKind} from "../SceneryResolverCore.js";
import {deriveVariants} from "../SceneryVariants.js";

/** Compute the number of instances the region whose `path_id` is `regionID` would place, over the
 *  FULL set of scenery regions collected from `terrainObjects` (so cross-region arbitration with
 *  overlapping regions is honoured). Full density (mpp 0) — the intrinsic density. Returns null
 *  when the region isn't active/placeable (no active scenery feature, no resolvable kinds, etc.).
 *  `resolveKind` supplies the resolved kind for a kind id (the renderer's `ResolveSceneryKind`). */
export function SceneryRegionPlacementCount(
	terrainObjects: MapObject_ForTerrain[],
	resolvableKinds: ReadonlySet<string>,
	resolveKind: (kindID: string) => ResolvedSceneryKind,
	regionID: string,
): number | null {
	const regions = collectSceneryRegions(terrainObjects, resolvableKinds);
	const region = regions.find(r=>r.id == regionID);
	if (region == null) return null;
	// If this region carries no kinds it can't place anything itself (suppression-only).
	if (!region.kinds || region.kinds.length == 0) return 0;
	return countForRegion(regions, resolveKind, region);
}

/** Internal: run the placer over a single tile covering the region's bbox and read the region's
 *  own placed count. Shared so the whole-map `regions` set + one `resolveKind` need resolving once. */
function countForRegion(
	regions: SceneryRegion[],
	resolveKind: (kindID: string) => ResolvedSceneryKind,
	region: SceneryRegion,
): number {
	const bbox = region.bbox;
	// A single tile covering the bbox (inflated by the feather reach + a small margin so the
	// bbox edge cells are full density). The placer's D5 margin already arbitrates edge collisions;
	// using the bbox directly keeps the tile minimal (and mpp 0 = full density, no rank thinning).
	const size = Math.max(bbox.width, bbox.height) + 200;
	const minX = bbox.x - 100;
	const minY = bbox.y - 100;
	const result = placeSceneryTile({
		regions,
		budget: new SceneryBudgetConfig({maxInstancesPerTile: 1_000_000, maxInstancesTotal: 10_000_000}),
		minX, minY, tileSize: size, mpp: 0,
		resolveKind: (id): PlacementKindLike | null => {
			const k = resolveKind(id);
			// PlaceSceneryKindLike needs id/spacing/presence/jitter/seed/collision — a ResolvedSceneryKind
			// structurally satisfies it, but we return null for unresolvable to match the renderer.
			return k == null ? null : (k as unknown as PlacementKindLike);
		},
		resolveVariants: (id)=>{
			const k = resolveKind(id);
			return k == null ? null : deriveVariants(k);
		},
		collectRegionCounts: true,
	});
	return result.placedByRegion.get(region.id) ?? 0;
}
